import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { TEAM_GAME_LOGS } from "../../constants/routes";

import MultiSelectDropdown from "../MultiSelectDropdown";
import Pill from "../Pill";
import Spinner from "../Spinner";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";
import api from "../../utils/api";
import { useParams } from "react-router-dom";
import { useReportIssue } from "../../hooks/useReportIssue";
import { ReportIssueModal } from "../../components/ReportIssueModal";


interface TeamData {
  SportCode: string,
  TeamName: string,
  TeamCode: string,
  stat_mapping: {
    [key: string | number]: any;
  }
}

const TeamGameLogsview =(props: { TeamData: TeamData | null | undefined }) => {
      const [selectedFilters, setSelectedFilters] = useState<IFilters>({ seasons: [], substat: [], stat: [] });
      const [teamgameLogsRes, setteamGameLogsRes] = useState<TeamGameLogsRes | null>(null);
      const [loading, setLoading] = useState<boolean>(false);
      const [errorStatus, setErrorStatus] = useState<number | null>(null);
      const [errorMessage, setErrorMessage] = useState<string | null>(null);
      const [isLoading, setisLoading] = useState(false);
      const { sportsCode, teamCode } = useParams();
        const [itemsPerPage, setItemsPerPage] = useState<number>(10);

      const formatNumericValue = (value: any): string | number => {
    if (value === null || value === undefined) return '-';
    if (typeof value !== 'number') return value;
    
    if (Number.isInteger(value)) {
      return value;
    }
    
    return parseFloat(value.toFixed(2));
  };

  function handleDropdownChange(key: any, value: any) {
    const updatedFilters: any = { ...selectedFilters };
    if (key === "seasons" || key === "substat" || key === "stat") {
      updatedFilters[key] = updatedFilters[key].includes(value)
        ? updatedFilters[key].filter((item: any) => item !== value)
        : [...updatedFilters[key], value];
    }

    setSelectedFilters(updatedFilters);
    if (!teamgameLogsRes) return;
  }

    const filteredData = useMemo(() => {
      const filtered = (teamgameLogsRes?.stats ?? []).filter((stat) =>
        selectedFilters.seasons.length > 0
          ? selectedFilters.seasons.includes(String(stat.season))
          : true
      );
      
      return filtered.map(stat => {
        const formattedStat: any = {};
        Object.keys(stat).forEach(key => {
          formattedStat[key] = formatNumericValue(stat[key]);
        });
        return formattedStat;
      });
    }, [teamgameLogsRes, selectedFilters.seasons]);
  
    const handleDeleteFilter = (filter: string) => {
      if (selectedFilters?.seasons?.includes(filter)) {
        setSelectedFilters({
          ...selectedFilters,
          seasons: selectedFilters.seasons.filter((season) => season !== filter)
        });
        return;
      }
    
    const isStatCategory = selectedFilters?.stat?.includes(filter);

    if (isStatCategory) {
      const categoryStats = teamgameLogsRes?.stat_mapping?.[filter] || [];
      const updatedStat = selectedFilters.stat.filter((s: string) => s !== filter);
      const updatedSubstat = selectedFilters.substat.filter(
        (substat: string) => !categoryStats.includes(substat)
      );
      
      setSelectedFilters({
        ...selectedFilters,
        stat: updatedStat,
        substat: updatedSubstat
      });
      return;
    }
     if (selectedFilters?.substat?.includes(filter)) {
      setSelectedFilters({
        ...selectedFilters,
        substat: selectedFilters.substat.filter((s: string) => s !== filter)
      });
      return;
    }
};

  const handleClearAllFilters = () => {
    setSelectedFilters({ seasons: [], substat: [], stat: [] });
  };

  const calculateTotals = () => {
    const filteredStats = (teamgameLogsRes?.stats ?? [])
      .filter((stat) => selectedFilters.seasons.length > 0 ? selectedFilters.seasons.includes(String(stat.season)) : true);
    
    if (filteredStats.length === 0) return {
      season: "TOTAL",
      game_date: "-", 
      opponent_team_name: "-"
    };
    
    const totals: any = {
      season: "TOTAL",
      game_date: "-",
      opponent_team_name: "-"
    };
    
    Object.keys(filteredStats[0] || {}).forEach(key => {
      if (!["season", "game_date", "opponent_team_name"].includes(key)) {
        const isPercentageStat = key.toLowerCase().includes('%');
        
        if (isPercentageStat) {
          const sum = filteredStats.reduce((acc, stat) => {
            const value = stat[key];
            return acc + (typeof value === 'number' && !isNaN(value) ? value : 0);
          }, 0);
          const count = filteredStats.filter(stat => {
            const value = stat[key];
            return typeof value === 'number' && !isNaN(value);
          }).length;
          const average = count > 0 ? (sum / count) : 0;
          totals[key] = formatNumericValue(average);
        } else {
          const sum = filteredStats.reduce((acc, stat) => {
            const value = stat[key];
            return acc + (typeof value === 'number' && !isNaN(value) ? value : 0);
          }, 0);
          totals[key] = formatNumericValue(sum);
        }
      }
    });
    
    return totals;
  };


  const getTotalsRowData = () => {
    return [calculateTotals()];
  };

 const getColDefs = () => {
  const totals = calculateTotals();

  // Base pinned columns
  const fixedColumns = [
    {
      field: "season",
      headerName: "Season",
      pinned: "left" as const,
      width: 100,
      sortable: true,
      valueFormatter: (params: any) =>
        params.node.rowPinned === "top" ? "TOTAL" : params.value ?? "-"
    },
    {
      field: "game_date",
      headerName: "Game Date",
      pinned: "left" as const,
      width: 120,
      sortable: true,
      valueFormatter: (params: any) =>
        params.node.rowPinned === "top" ? "-" : params.value ?? "-"
    },
    {
      field: "opponent_team_name",
      headerName: "Opponent Team",
      pinned: "left" as const,
      width: 180,
      sortable: true,
      valueFormatter: (params: any) =>
        params.node.rowPinned === "top" ? "-" : params.value ?? "-"
    },
    {
      field: "game_result",
      headerName: "Result",
      pinned: "left" as const,
      width: 100,
      sortable: true,
      valueFormatter: (params: any) => {
        if (params.node.rowPinned === "top") return "-";

        const value = params.value;

        if (value === 1) return "W";
        if (value === 0) return "L";

        return "-";
      }
    },
    {
      field: "team_opponent_score_string",
      headerName: "Score",
      pinned: "left" as const,
      width: 100,
      sortable: false,
      valueFormatter: (params: any) =>
        params.node.rowPinned === "top" ? "-" : params.value ?? "-"
    }
  ];

  // Dynamic stat columns
  const statColumns = Object.keys(teamgameLogsRes?.stats[0] ?? {})
    .filter(
      (key) => !["season", "game_date", "opponent_team_name", "game_result", "team_opponent_score_string"].includes(key)
    )
 
    .filter((key) => {
      if (!selectedFilters.stat.length) return true;
      const allowedStats = selectedFilters.stat
        .map((stat) => teamgameLogsRes?.stat_mapping?.[stat] ?? [])
        .flat();
      return allowedStats.includes(key);
    })
    //  Apply substat filter
    .filter((key) => {
      if (!selectedFilters.substat.length) return true;
      return selectedFilters.substat.includes(key);
    })
    //  Remove zero-total columns
    .filter((key) => {
      const total = totals[key];
      const num = typeof total === "number" ? total : parseFloat(total);
      return !isNaN(num) && num !== 0;
    })
    // SORT COLUMNS BY TOTAL (DESCENDING)
    .sort((a, b) => {
      const valA = Number(totals[a]) || 0;
      const valB = Number(totals[b]) || 0;
      return valB - valA; // descending
    })
    // 🔹 Convert to colDefs
    .map((key) => ({
      field: key,
      headerName: key,
      sortable: true,
      resizable: true,
      width: 120,
      valueFormatter: (params: any) =>
        params.value === null || params.value === undefined
          ? "-"
          : params.value
    }));

  return [...fixedColumns, ...statColumns];
};

  const getGameLogs = async (season?: number | null) => {    
    try {
     setisLoading(true);
     const payload:any ={
        SportCode: props.TeamData?.SportCode,
        Season : season || null,
        TeamCode: teamCode,
      }
      const teamgameLogsRes: { data: TeamGameLogsRes } = await api.post(TEAM_GAME_LOGS, payload);
      console.log("gamelog ", teamgameLogsRes)
      setteamGameLogsRes(teamgameLogsRes.data);
  }
   catch (err: any) {
      console.error("Failed to fetch team records:", err);
      setErrorStatus(err?.response?.status ?? 500);
      setErrorMessage(err?.response?.data?.detail || "Something went wrong while fetching team records.");
    }
    finally {
      setisLoading(false);
    }
}

useEffect(() => {
  if (!props.TeamData?.SportCode || !teamCode) return;
  getGameLogs();
}, [props.TeamData?.SportCode, teamCode]);


  const reportIssue = useReportIssue();

    const handleReportIssue = (errorContext?: string) => {
    console.log('Report issue clicked!');
    reportIssue.openModal({
      ComponentSource: 'TeamDashboard - GameLogsView',
      ErrorContext: errorContext || errorMessage || '',
      UserContext: {
        sportsCode,
        errorStatus,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      },
    });
  };

    const handleItemsPerPageChange = (_itemsPerPage: number) => {
    setItemsPerPage(_itemsPerPage);
  };

    return(
    <div className="flex flex-col gap-2">
      <div className="flex flex-row justify-between items-start">
        <div className="flex flex-row justify-start items-center gap-2 w-full">
          <div className="text-sm font-medium">Filters:-</div>
          <div className="flex gap-2 flex-wrap max-w-[40rem] px-2">
            {([...selectedFilters.stat, ...selectedFilters.seasons, ...selectedFilters.substat])
              .filter((f) => f !== null)
              .map((filter, index) => (
                <Pill key={index} text={filter} showCross={true} onClick={() => handleDeleteFilter(filter)} />
              ))}
          </div>

          {(selectedFilters.stat.length || selectedFilters.seasons.length || selectedFilters.substat.length) > 0 && (
            <button
              onClick={handleClearAllFilters}
              className="ml-2 text-xs text-red-600 hover:text-red-800 underline"
            >
              Clear All
            </button>
          )}
        </div>
        <div className="flex flex-row justify-between gap-2 max-w-[480px] w-full">
          <MultiSelectDropdown 
            options={(teamgameLogsRes?.Seasons)?.map((_season) => String(_season)) ?? []} 
            options2={[]} 
            def="Select Seasons" 
            setSelectedOption={handleDropdownChange} 
            selectedOption={selectedFilters} 
            filterKey="seasons" 
            disabled={false} 
          />
          <MultiSelectDropdown 
            options={Object.keys(teamgameLogsRes?.stat_mapping ?? {}) ?? []} 
            options2={[]} 
            def="Select Category" 
            setSelectedOption={handleDropdownChange} 
            selectedOption={selectedFilters} 
            filterKey="stat" 
            disabled={false} 
          />
          <MultiSelectDropdown 
            options={selectedFilters.stat.map((stat) => teamgameLogsRes?.stat_mapping?.[stat]).flat()} 
            options2={[]} 
            def="Select Substat" 
            setSelectedOption={handleDropdownChange} 
            selectedOption={selectedFilters} 
            filterKey="substat" 
            disabled={!selectedFilters.stat} 
          />
        </div>
      </div>

      <div className="w-full overflow-x-auto rounded-lg">
        <div className="ag-theme-alpine" style={{ height: "500px", width: "100%" }}>
        {errorMessage && errorStatus >= 500 ? (
          <div className="bg-red-50 text-red-700 p-4 rounded text-sm mb-4">
            {errorMessage}{" "}
            <button
              className="underline text-blue-600 hover:text-blue-800"
              onClick={() =>
                handleReportIssue(
                  `Failed to fetch Team profile ( SportCode: ${sportsCode}, HTTP ${errorStatus})`
                )
              }
            >
              Click here to report this issue
            </button>
          </div>
        ) : (
          <>
            <AgGridReact
              rowData={filteredData}
              pinnedTopRowData={getTotalsRowData()}
              columnDefs={getColDefs()}
              defaultColDef={{ 
                width: 120,
                minWidth: 80,
                resizable: true,
                sortable: true,
                valueFormatter: (params) => {
                  const val = params.value;
                  return val === null || val === undefined ? '-' : val;
                },
                comparator: (valueA, valueB) => {
                  const isADash = valueA === "-" || valueA === null || valueA === undefined;
                  const isBDash = valueB === "-" || valueB === null || valueB === undefined;
                  
                  if (isADash && isBDash) return 0;
                  if (isADash) return 1;
                  if (isBDash) return -1;
                  
                  // STRICT CHECK: Only parse as number if string is purely numeric (no hyphens)
                  // This prevents dates like "2016-10-29" from being parsed as "2016"
                  const isNumericString = (val: any) => /^-?\d*\.?\d+$/.test(String(val));

                  if (isNumericString(valueA) && isNumericString(valueB)) {
                    return parseFloat(String(valueA)) - parseFloat(String(valueB));
                  }
                  
                  return String(valueA).localeCompare(String(valueB));
                },
              }}
              getRowStyle={(params) => {
                if (params.node.rowPinned === 'top') {
                  return { 
                    backgroundColor: '#f3f4f6', 
                    fontWeight: 'bold',
                    borderBottom: '2px solid #d1d5db'
                  };
                }
                return {};
              }}
              enableRangeSelection={true}
              animateRows={true}
              pagination={true}
              paginationPageSize={itemsPerPage}
              paginationPageSizeSelector={[10, 25, 50, 100]}
              onPaginationChanged={(params) => {
                const newPageSize = params.api.paginationGetPageSize();
                if (newPageSize !== itemsPerPage) {
                  setItemsPerPage(newPageSize);
                }
              }}
              theme={themeQuartz}
              loadingOverlayComponent={Spinner}
              loading={isLoading}
              suppressHorizontalScroll={false}
              alwaysShowHorizontalScroll={true}
            />
          </>
        )}
        </div>
      </div>
      
      <ReportIssueModal
        isOpen={reportIssue.isModalOpen}
        onClose={reportIssue.closeModal}
        onSubmit={reportIssue.handleSubmit}
        issue={reportIssue.issue}
        setIssue={reportIssue.setIssue}
        feedbackMessage={reportIssue.feedbackMessage}
        feedbackType={reportIssue.feedbackType}
        isLoading={reportIssue.isLoading}
      />
    </div>
    );
}

export default TeamGameLogsview;